// ---------------------------------------------------------------------------
// SubtopicsScroll — area de conteudo a direita: renderiza todos os
// SubTopicos do Topic atual em sequencia, dentro do proprio container
// rolavel (`.contentScroll`). E' esse container que e' o root do
// IntersectionObserver e o alvo do scrollIntoView programatico.
//
// Por que container interno em vez de scroll na pagina:
// o app envolve a Main com PerfectScrollbar, o que confunde tanto o
// sticky do rail quanto o scrollIntoView (que rola a pagina inteira,
// nao o conteudo). Encapsular o scroll aqui resolve os dois.
//
// Scroll-spy:
//   - IntersectionObserver com `root: scrollEl`, rootMargin recortando
//     uma "faixa ativa" nos primeiros ~45% do scroll container.
//   - SubTopic ativo = primeiro top-down que ainda intersecta a faixa.
//   - Suprimido durante scroll programatico (700ms smooth / 60ms instant)
//     para evitar jitter nos itens intermediarios.
//
// Exposicao:
//   - `scrollToSub(slug, behavior)` via imperative handle (ref).
// ---------------------------------------------------------------------------

import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import cx from "classnames";
import moment from "moment/moment";
import { useTranslation } from "react-i18next";
import Button from "@material-ui/core/Button";
import { Refresh } from "@material-ui/icons";
import documentationV3Style from "../../../assets/jss/material-dashboard-pro-react/views/documentationV3Style";
import {
  useOverlayScrollStyles,
  useNativeWheelScroll,
} from "../inputs_v3_components";

const useStyles = makeStyles(documentationV3Style);

function stripHtml(html) {
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || "";
}

function matchesQuery(text, q) {
  if (!q) return true;
  return text.toLowerCase().includes(q.toLowerCase());
}

function subMatchesSearch(s, topic, q) {
  return (
    !q ||
    matchesQuery(s.title, q) ||
    matchesQuery(stripHtml(s.content), q) ||
    matchesQuery(topic.title, q)
  );
}

function formatDriftDetail(drift, t) {
  if (!drift) return "";
  const parts = [];
  if (drift.modified?.length) {
    parts.push(
      t("modeling_documentation.subtopic_drift_modified", {
        items: drift.modified.join(", "),
      }),
    );
  }
  if (drift.added?.length) {
    parts.push(
      t("modeling_documentation.subtopic_drift_added", {
        items: drift.added.join(", "),
      }),
    );
  }
  if (drift.removed?.length) {
    parts.push(
      t("modeling_documentation.subtopic_drift_removed", {
        items: drift.removed.join(", "),
      }),
    );
  }
  return parts.join("\n");
}

const SubtopicsScroll = forwardRef(function SubtopicsScroll(
  { topic, query, onSubInView, onRegenSubtopic, regenInflight, canRegen },
  ref,
) {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const overlayScrollClasses = useOverlayScrollStyles();
  const wheelRef = useNativeWheelScroll();

  const scrollElRef = useRef(null);
  const observerRef = useRef(null);
  const suppressSpyRef = useRef(false);
  const suppressTimeoutRef = useRef(null);

  // Compoe os refs (overlayScroll precisa de className no proprio
  // scroll container; o wheel hook anexa um listener nativo nesse
  // mesmo no; precisamos guardar o no para queries .querySelector).
  const setScrollEl = (node) => {
    scrollElRef.current = node;
    wheelRef(node);
  };

  const visibleSubs = useMemo(
    () =>
      topic
        ? topic.subtopics.filter((s) => subMatchesSearch(s, topic, query))
        : [],
    [topic, query],
  );

  useImperativeHandle(ref, () => ({
    scrollToSub(slug, behavior = "smooth") {
      const scrollEl = scrollElRef.current;
      if (!scrollEl) return;
      const target = scrollEl.querySelector(`[data-subslug="${slug}"]`);
      if (!target) return;

      suppressSpyRef.current = true;
      clearTimeout(suppressTimeoutRef.current);

      // Cálculo manual da posicao relativa em vez de scrollIntoView:
      // garante que rolemos o scroll container, nao um ancestral.
      // `offsetTop` do target (relativo ao primeiro ancestral posicionado)
      // funciona porque .content nao tem position; offsetTop do target
      // referencia o scroll container diretamente.
      const top = target.offsetTop;
      scrollEl.scrollTo({ top: top - 12, behavior });

      suppressTimeoutRef.current = setTimeout(
        () => {
          suppressSpyRef.current = false;
        },
        behavior === "instant" ? 60 : 700,
      );
    },
  }));

  // (Re)constroi o observer toda vez que o conjunto de SubTopicos
  // renderizados muda (troca de Topic ou mudanca no filtro de busca).
  // O root e o proprio scroll container — sem isso, o IO trabalharia
  // contra o viewport e nao reagiria ao scroll interno.
  useEffect(() => {
    const scrollEl = scrollElRef.current;
    if (!scrollEl) return undefined;
    if (observerRef.current) observerRef.current.disconnect();

    const targets = Array.from(scrollEl.querySelectorAll("[data-subslug]"));
    if (!targets.length) return undefined;

    const visibleMap = new Map();

    const observer = new IntersectionObserver(
      (entries) => {
        if (suppressSpyRef.current) return;
        entries.forEach((entry) => {
          const slug = entry.target.getAttribute("data-subslug");
          if (entry.isIntersecting) {
            visibleMap.set(slug, entry.intersectionRatio);
          } else {
            visibleMap.delete(slug);
          }
        });

        let chosen = null;
        for (const target of targets) {
          const slug = target.getAttribute("data-subslug");
          if (visibleMap.has(slug)) {
            chosen = slug;
            break;
          }
        }
        if (chosen) onSubInView(chosen);
      },
      {
        root: scrollEl,
        // Faixa ativa = primeiros ~45% do scroll container.
        rootMargin: "0px 0px -55% 0px",
        threshold: [0, 0.1, 0.5, 1],
      },
    );
    targets.forEach((t) => observer.observe(t));
    observerRef.current = observer;

    return () => observer.disconnect();
  }, [visibleSubs, onSubInView]);

  if (!topic) {
    return (
      <div
        ref={setScrollEl}
        className={cx(
          classes.contentScroll,
          overlayScrollClasses.overlayScroll,
        )}
      >
        <div className={classes.empty}>
          {t("modeling_documentation.topic_not_found")}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={setScrollEl}
      className={cx(classes.contentScroll, overlayScrollClasses.overlayScroll)}
    >
      <div className={classes.content}>
        <div className={classes.topicHeader}>
          <div className={classes.topicEyebrow}>
            {t("modeling_documentation.topic_eyebrow", {
              position: topic.position,
            })}
          </div>
          <h2 className={classes.topicTitle}>{topic.title}</h2>
        </div>

        {visibleSubs.length === 0 ? (
          <div className={classes.empty}>
            {t("modeling_documentation.no_subtopic_match")}
          </div>
        ) : (
          visibleSubs.map((s, i) => (
            <section
              key={s.slug}
              data-subslug={s.slug}
              className={classes.subtopicBlock}
            >
              <div className={classes.subtopicHeader}>
                <h3 className={classes.subtopicTitle}>
                  <span className={classes.subtopicCounter}>
                    {topic.position}.{i + 1}
                  </span>
                  {s.title}
                </h3>
                <div className={classes.subtopicHeaderRight}>
                  {canRegen && s.drift?.is_stale && (
                    <span
                      className={classes.subtopicStalePill}
                      title={
                        t("modeling_documentation.subtopic_stale_tooltip") +
                        "\n" +
                        formatDriftDetail(s.drift, t)
                      }
                    >
                      {t("modeling_documentation.subtopic_stale_pill")}
                    </span>
                  )}
                  {s.last_regen_at && (
                    <span className={classes.subtopicUpdated}>
                      {t("modeling_documentation.subtopic_updated_at", {
                        date: moment(s.last_regen_at)
                          .locale(i18n.language || "pt")
                          .format("DD/MMM/YYYY"),
                      })}
                    </span>
                  )}
                  {canRegen && onRegenSubtopic && (
                    // ``manual_lock`` so bloqueia o batch automatico
                    // (Wave 4 PR-D). Editor manual via UI (DEV/IMG) e
                    // sempre permitido — inclui SubTopics sem
                    // source_blocks (editorial puro: Estudios de Caso,
                    // Informacion general, etc.).
                    <Button
                      className={classes.subtopicRegenBtn}
                      size="small"
                      variant="outlined"
                      startIcon={<Refresh fontSize="small" />}
                      disabled={!!regenInflight?.[s.id]}
                      onClick={() =>
                        onRegenSubtopic(s.id, s.title, s.content || "")
                      }
                    >
                      {regenInflight?.[s.id]
                        ? t("modeling_documentation.subtopic_regenerating")
                        : t("modeling_documentation.subtopic_regenerate")}
                    </Button>
                  )}
                </div>
              </div>
              <div
                className={classes.subtopic}
                // SubTopic.content e HTML curado pela skill `documentation-writer`
                // (V2) ou regenerado pelo engine V3 (Wave 4). Whitelist de
                // tags/classes validada no backend; rendering com
                // dangerouslySetInnerHTML eh consciente.
                dangerouslySetInnerHTML={{ __html: s.content }}
              />
            </section>
          ))
        )}
      </div>
    </div>
  );
});

SubtopicsScroll.propTypes = {
  topic: PropTypes.object,
  query: PropTypes.string,
  onSubInView: PropTypes.func.isRequired,
  onRegenSubtopic: PropTypes.func,
  regenInflight: PropTypes.object,
  canRegen: PropTypes.bool,
};

export default SubtopicsScroll;
